/**
 * 요금제 플랜 섹션 컴포넌트
 * ServiceDetailDialog에서 분리
 */
import React from 'react';
import { Badge } from '@/components/ui/Badge';

interface QuotaPlan {
  id: number;
  plan_name: string;
  quota_type: string;
  quota_limit: number;
  monthly_base: number;
  price_per_unit: number;
  is_active: boolean;
  description?: string;
}

interface PricingPlansSectionProps {
  quotaPlans: QuotaPlan[];
  metadataPricingPlans?: any[];
  loading?: boolean;
}

const PlanCard: React.FC<{
  plan: QuotaPlan | any;
  variant: 'db' | 'metadata';
}> = ({ plan, variant }) => {
  const bgColor = variant === 'db' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200';
  const badgeLabel = variant === 'db' ? (plan.is_active ? '활성' : '비활성') : '메타데이터';
  const badgeVariant = variant === 'db' ? (plan.is_active ? 'active' : 'inactive') : 'active';

  return (
    <div className={`${bgColor} border rounded-md p-3`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-sm">{plan.plan_name}</span>
        <Badge variant={badgeVariant}>{badgeLabel}</Badge>
      </div>
      <div className="text-xs text-gray-600 space-y-1">
        <div>쿼타 타입: {plan.quota_type}</div>
        {plan.quota_limit > 0 && (
          <div>쿼타 한도: {plan.quota_limit === -1 ? '무제한' : plan.quota_limit.toLocaleString()}</div>
        )}
        {plan.monthly_base > 0 && (
          <div>월 기본 요금: {Number(plan.monthly_base).toFixed(1)} 크레딧</div>
        )}
        {plan.price_per_unit > 0 && (
          <div>단위당 가격: {Number(plan.price_per_unit).toFixed(1)} 크레딧</div>
        )}
        {plan.monthly_base === 0 && plan.price_per_unit === 0 && (
          <div className="text-green-600 font-semibold">무료 플랜</div>
        )}
        {plan.description && (
          <div className="text-gray-500 italic mt-1">{plan.description}</div>
        )}
      </div>
    </div>
  );
};

export const PricingPlansSection: React.FC<PricingPlansSectionProps> = ({
  quotaPlans,
  metadataPricingPlans,
  loading = false,
}) => {
  if (loading) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">요금제 플랜</h3>
        <div className="text-sm text-gray-500">플랜 정보를 불러오는 중...</div>
      </div>
    );
  }

  const hasQuotaPlans = quotaPlans.length > 0;
  const hasMetadataPlans = metadataPricingPlans && Array.isArray(metadataPricingPlans) && metadataPricingPlans.length > 0;

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">요금제 플랜</h3>

      {/* DB에서 가져온 플랜 표시 */}
      {hasQuotaPlans && (
        <div className="space-y-2 mb-3">
          {quotaPlans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} variant="db" />
          ))}
        </div>
      )}

      {/* metadata에서 가져온 pricing_plans 표시 */}
      {hasMetadataPlans && (
        <div className="space-y-2">
          {metadataPricingPlans.map((plan: any, index: number) => {
            // DB 플랜과 중복 체크 (plan_name으로)
            const isDuplicate = quotaPlans.some(p => p.plan_name === plan.plan_name);
            if (isDuplicate) return null;

            return <PlanCard key={`metadata-${index}`} plan={plan} variant="metadata" />;
          })}
        </div>
      )}

      {/* 플랜이 하나도 없는 경우 */}
      {!hasQuotaPlans && !hasMetadataPlans && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <p className="text-sm text-gray-600">등록된 플랜이 없습니다.</p>
        </div>
      )}
    </div>
  );
};

export default PricingPlansSection;
